import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { checkComponentPermission } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import LeaveStatusBadge from './LeaveStatusBadge';
import { LEAVE_TYPE_LABELS, LEAVE_STATUS } from '../constants';

const SYSTEM_AUTO_LEAVE_REASON = 'SYSTEM_AUTO_APPROVED';

const LeaveTable = ({
  leaves,
  onCancelLeave,
  onEditLeave,
  showEmployee = false,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();

  const isUnplannedLeave = (leaveItem) => {
    if (!leaveItem?.startDate || !leaveItem?.createdAt) return false;
    // "Unplanned" means the request was created after the leave's start date.
    const start = dayjs(leaveItem.startDate).startOf('day');
    const created = dayjs(leaveItem.createdAt).startOf('day');
    return created.isAfter(start);
  };

  const isSystemApprovedLeave = (leaveItem) => leaveItem?.reason === SYSTEM_AUTO_LEAVE_REASON;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {showEmployee && <TableCell>Employee</TableCell>}
              <TableCell>Leave Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Days</TableCell>
              <TableCell>Status</TableCell>
              {showEmployee && <TableCell>Reviewed By</TableCell>}
              {showEmployee && <TableCell>Reviewed Date</TableCell>}
              <TableCell sx={{ pl: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaves && leaves.length > 0 ? (
              leaves.map((leave) => (
                <TableRow key={leave.id} hover>
                  {showEmployee && <TableCell>{leave.employee?.userName || leave.employee?.email || '-'}</TableCell>}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <span>
                        {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                        {leave.isHalfDay && ` (Half Day)`}
                      </span>
                      {isUnplannedLeave(leave) && (
                        <Chip label="Unplanned" color="warning" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{formatDate(leave.startDate)}</TableCell>
                  <TableCell>{formatDate(leave.endDate)}</TableCell>
                  <TableCell>{leave.totalDays}</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <LeaveStatusBadge status={leave.status} />
                      {isSystemApprovedLeave(leave) && (
                        <Chip label="System Approved" color="info" size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {leave.approver && leave.approver.id === currentUserId
                        ? 'Me'
                        : leave.approver?.userName || leave.approver?.email || '-'}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{formatDate(leave.reviewedAt || leave.approvedAt)}</TableCell>}
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leaves/${leave.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {!showEmployee && leave.status === LEAVE_STATUS.PENDING && leave.userId === currentUserId && (
                        <>
                          {checkComponentPermission('UPDATE_LEAVE') && (
                            <Tooltip title="Edit Leave">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditLeave(leave.id);
                                }}
                              >
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {checkComponentPermission('DELETE_LEAVE') && (
                            <Tooltip title="Delete Leave">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelLeave(leave.id);
                                }}
                              >
                                <Iconify icon="material-symbols:delete" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={showEmployee ? 9 : 6} align="center">
                  No leave requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

LeaveTable.propTypes = {
  leaves: PropTypes.array.isRequired,
  onCancelLeave: PropTypes.func,
  onEditLeave: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
};

export default LeaveTable;
